import { useEffect } from 'react';

/**
 * El botón Atrás de Android.
 *
 * En un navegador el botón atrás es del navegador. Dentro de Capacitor es del
 * teléfono, y si nadie lo atiende **cierra la aplicación**. Ese es el problema
 * a resolver: alguien que está cargando un trabajo, toca atrás esperando cerrar
 * un panel, y pierde todo lo cargado sin siquiera una pregunta.
 *
 * El orden que se respeta es el que la persona espera: se deshace lo último que
 * hizo, no lo primero.
 *
 *   teclado abierto   → lo cierra Android antes de avisarnos
 *   panel o pantalla  → se cierra eso
 *   pestaña adentro   → vuelve a Inicio
 *   formulario cargado→ pregunta antes de descartar
 *   nada de lo anterior → recién ahí sale
 *
 * El teclado no aparece en el código porque no llega hasta acá: Android lo
 * cierra él mismo y consume el evento. Eso hay que **comprobarlo en un teléfono**
 * y no darlo por hecho, que es justo lo que este archivo no puede hacer.
 */

type Guardia = () => boolean;

/**
 * Pila de guardias. El último que se registra es el primero en atender, porque
 * es el más profundo: el panel que se abrió recién se cierra antes que la
 * pantalla que lo contiene.
 */
const guardias: Guardia[] = [];

/**
 * Registra un guardia mientras el componente esté montado.
 *
 * `manejar` devuelve `true` si consumió el gesto —cerró algo, preguntó algo— y
 * `false` si no le correspondía y hay que seguir bajando por la pila.
 */
export function useBotonAtras(manejar: Guardia, activo = true): void {
  useEffect(() => {
    if (!activo) return;
    guardias.push(manejar);
    return () => {
      const i = guardias.lastIndexOf(manejar);
      if (i >= 0) guardias.splice(i, 1);
    };
  });
}

/** Resuelve un toque de Atrás. `false` = nadie lo atendió, corresponde salir. */
export function atenderAtras(): boolean {
  for (let i = guardias.length - 1; i >= 0; i--) {
    const g = guardias[i];
    if (g && g()) return true;
  }
  return false;
}

/** Sólo para las pruebas: registra sin montar un componente. */
export function _registrarGuardia(fn: Guardia): void {
  guardias.push(fn);
}

/** Sólo para las pruebas: deja la pila como estaba. */
export function _limpiarGuardias(): void {
  guardias.length = 0;
}

/**
 * Conecta la pila con Android. En la web no hace nada: `@capacitor/app` sólo
 * emite el evento dentro de la aplicación nativa.
 */
export async function conectarBotonAtras(): Promise<void> {
  const { App } = await import('@capacitor/app');
  await App.addListener('backButton', () => {
    if (!atenderAtras()) void App.exitApp();
  });
}
